/* Southern Atelier – header, announcement, search (no dependencies) */
(() => {
  if (window.__saHeaderLoaded) return;
  window.__saHeaderLoaded = true;

  const TAB_KEY = 'sa:active-tab';
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
  };

  /* ---------- Announcement ---------- */
  class SaAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.sa-announce__item')];
      if (this.items.length < 2) return;
      this.i = 0;
      const ms = Number(this.dataset.interval) || 5000;
      this.timer = setInterval(() => this.next(), ms);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => { clearInterval(this.timer); this.timer = setInterval(() => this.next(), ms); });
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.items[this.i].classList.remove('is-active');
      this.i = (this.i + 1) % this.items.length;
      this.items[this.i].classList.add('is-active');
    }
  }
  if (!customElements.get('sa-announcement')) customElements.define('sa-announcement', SaAnnouncement);

  /* ---------- Header ---------- */
  class SaHeader extends HTMLElement {
    connectedCallback() {
      this.initTabs();
      this.initMega();
      this.initDrawer();
      this.initMobileSearch();
      this.initCartButton();
    }

    /* Collection tabs: server marks the tab matching the page; otherwise use the last chosen tab */
    initTabs() {
      const serverTab = this.dataset.activeTab;
      const tabsLinks = this.querySelectorAll('.sa-header__tab');
      const matched = [...tabsLinks].some((a) => location.pathname.startsWith(a.getAttribute('href')) && a.getAttribute('href') !== '#');
      const saved = store.get(TAB_KEY);
      const active = matched ? serverTab : (saved ?? serverTab);
      this.setTab(active);
      this.querySelectorAll('[data-sa-tab]').forEach((el) => {
        el.addEventListener('click', (e) => {
          const idx = el.dataset.saTab;
          store.set(TAB_KEY, idx);
          if (el.tagName === 'BUTTON') { e.preventDefault(); this.setTab(idx); }
        });
      });
    }
    setTab(idx) {
      if (idx == null) return;
      this.querySelectorAll('[data-sa-tab]').forEach((el) => {
        const on = el.dataset.saTab === String(idx);
        el.classList.toggle('is-active', on);
        if (el.getAttribute('role') === 'tab') el.setAttribute('aria-selected', on);
      });
      this.querySelectorAll('[data-sa-tab-pane]').forEach((p) => { p.hidden = p.dataset.saTabPane !== String(idx); });
      // If the requested pane doesn't exist, show the first one
      const panes = [...this.querySelectorAll('.sa-header__navs [data-sa-tab-pane]')];
      if (panes.length && panes.every((p) => p.hidden)) panes[0].hidden = false;
    }

    /* Mega menu: hover intent on desktop, keyboard + click support */
    initMega() {
      const items = this.querySelectorAll('[data-sa-mega]');
      let openTimer, closeTimer;
      const closeAll = (except) => items.forEach((it) => {
        if (it === except) return;
        it.classList.remove('is-open');
        it.querySelector(':scope > .sa-nav__link')?.setAttribute('aria-expanded', 'false');
      });
      items.forEach((item) => {
        const link = item.querySelector(':scope > .sa-nav__link');
        const open = () => { closeAll(item); item.classList.add('is-open'); link?.setAttribute('aria-expanded', 'true'); };
        item.addEventListener('mouseenter', () => { clearTimeout(closeTimer); openTimer = setTimeout(open, 120); });
        item.addEventListener('mouseleave', () => { clearTimeout(openTimer); closeTimer = setTimeout(() => closeAll(), 150); });
        item.addEventListener('focusin', open);
        link?.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAll(); link.focus(); } });
      });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) closeAll(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
    }

    /* Mobile drawer */
    initDrawer() {
      const drawer = this.querySelector('[data-sa-drawer]');
      const opener = this.querySelector('[data-sa-drawer-open]');
      if (!drawer || !opener) return;
      const open = () => {
        drawer.hidden = false;
        document.documentElement.classList.add('sa-lock');
        requestAnimationFrame(() => drawer.classList.add('is-open'));
        opener.setAttribute('aria-expanded', 'true');
        drawer.querySelector('.sa-drawer__close')?.focus();
      };
      const close = () => {
        drawer.classList.remove('is-open');
        opener.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('sa-lock');
        setTimeout(() => { drawer.hidden = true; drawer.querySelectorAll('.sa-dmenu__sub').forEach((s) => { s.classList.remove('is-open'); s.hidden = true; }); }, 350);
        opener.focus();
      };
      opener.addEventListener('click', open);
      drawer.querySelectorAll('[data-sa-drawer-close]').forEach((b) => b.addEventListener('click', close));
      drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
      drawer.querySelectorAll('[data-sa-sub-open]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = drawer.querySelector('#' + btn.dataset.saSubOpen);
          if (!sub) return;
          sub.hidden = false;
          requestAnimationFrame(() => sub.classList.add('is-open'));
          sub.querySelector('[data-sa-sub-close]')?.focus();
        });
      });
      drawer.querySelectorAll('[data-sa-sub-close]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = btn.closest('.sa-dmenu__sub');
          sub.classList.remove('is-open');
          setTimeout(() => { sub.hidden = true; }, 300);
        });
      });
    }

    initMobileSearch() {
      const toggle = this.querySelector('[data-sa-search-toggle]');
      const box = this.querySelector('[data-sa-mobile-search]');
      if (!toggle || !box) return;
      toggle.addEventListener('click', () => {
        box.hidden = !box.hidden;
        if (!box.hidden) box.querySelector('input[type=search]')?.focus();
      });
    }

    initCartButton() {
      this.querySelectorAll('[data-sa-cart-open]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const drawer = document.getElementById('cart-drawer');
          if (drawer && typeof drawer.open === 'function') { e.preventDefault(); drawer.open(); }
          else if (drawer && typeof drawer.toggle === 'function') { e.preventDefault(); drawer.toggle(); }
          else { window.location.href = (window.Shopify?.routes?.root || '/') + 'cart'; }
        });
      });
    }
  }
  if (!customElements.get('sa-header')) customElements.define('sa-header', SaHeader);

  /* ---------- Predictive search ---------- */
  class SaSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-sa-search-input]');
      this.results = this.querySelector('[data-sa-search-results]');
      if (!this.input || !this.results) return;
      let t;
      this.input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.search(), 250); });
      this.input.addEventListener('focus', () => { if (this.results.innerHTML.trim()) this.results.hidden = false; });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.results.hidden = true; });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.results.hidden = true; this.input.blur(); } });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.hidden = true; this.results.innerHTML = ''; return; }
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=6&section_id=sa-predictive-search`;
      try {
        this.abort?.abort();
        this.abort = new AbortController();
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-sa-psearch]');
        this.results.innerHTML = inner ? inner.outerHTML : '';
        this.results.hidden = !inner;
      } catch (e) { /* aborted */ }
    }
  }
  if (!customElements.get('sa-search')) customElements.define('sa-search', SaSearch);
})();
